// pages/EmployeePerformancePage.tsx
import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Alert,
  Typography, 
  Paper, 
  Grid, 
  Card, 
  CardContent, 
  CardHeader,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Tabs,
  Tab,
  Divider,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  SelectChangeEvent
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  Assessment as AssessmentIcon,
  Person as PersonIcon,
  Download as DownloadIcon,
  AccessTime as AccessTimeIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useAuth } from '../contexts/AuthContext';
import trackingService from '../services/employeeTrackingService';
import timeClockService, { MonthlyUser, MonthlyDayRow } from '../services/timeClockService';
import { downloadPayslip } from '../utils/payslipExcel';
import { Bar, Line } from 'react-chartjs-2';
import { 
  Chart as ChartJS, 
  CategoryScale, 
  LinearScale, 
  BarElement, 
  LineElement,
  PointElement,
  Title, 
  Tooltip as ChartTooltip, 
  Legend
} from 'chart.js';
import { ChartData } from 'chart.js';
import api from '../services/api';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  ChartTooltip,
  Legend
);

interface EmployeeSummary {
    user_id: number;
    employee_name: string;
    items_received: number;
    orders_shipped: number;
    items_shipped: number;
    efficiency: number; // Items per day
  }

// Type for performance data
interface PerformanceData {
  id: number;
  user_id: number;
  employee_name: string;
  employee_email: string;
  date: string;
  items_received: number;
  orders_shipped: number;
  items_shipped: number;
  updated_at: string;
}

// Type for action data
interface ActionData {
  id: number;
  user_id: number;
  employee_name: string;
  employee_email: string;
  action_type: string;
  order_id: string;
  item_id: string;
  quantity: number;
  action_timestamp: string;
  additional_data: any;
  created_at: string;
}

const EmployeePerformancePage: React.FC = () => {
  const { currentUser } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [startDate, setStartDate] = useState<Date | null>(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)); // Last 30 days
  const [endDate, setEndDate] = useState<Date | null>(new Date());
  const [performanceData, setPerformanceData] = useState<PerformanceData[]>([]);
  const [actionData, setActionData] = useState<ActionData[]>([]);
  const [totalStats, setTotalStats] = useState({
    items_received: 0,
    orders_shipped: 0,
    items_shipped: 0
  });
  const [chartData, setChartData] = useState<ChartData<'line', number[], string>>({
    labels: [],
    datasets: []
  });
  const [actionType, setActionType] = useState<string>('all');

  // 出勤時數 (attendance) — owner-only tab (index 3).
  const currentTaipeiMonth = new Date()
    .toLocaleDateString('en-CA', { timeZone: 'Asia/Taipei', year: 'numeric', month: '2-digit' })
    .slice(0, 7); // YYYY-MM
  const [attMonth, setAttMonth] = useState<string>(currentTaipeiMonth);
  const [attData, setAttData] = useState<MonthlyUser[]>([]);
  const [attLoading, setAttLoading] = useState(false);
  // Row being edited in the 出勤時數 table. Times are Taipei wall-clock
  // 'YYYY-MM-DDTHH:mm' strings for <input type="datetime-local">.
  const [editRow, setEditRow] = useState<{ userId: number; name: string; row: MonthlyDayRow; inAt: string; outAt: string; note: string } | null>(null);
  const [editSaving, setEditSaving] = useState(false);

  const [employeesData, setEmployeesData] = useState<EmployeeSummary[]>([]);
    const [employeesLoading, setEmployeesLoading] = useState(false);
    const [employeeChartData, setEmployeeChartData] = useState<ChartData<'bar', number[], string>>({
    labels: [],
    datasets: []
    });

  // Handle tab change
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Format date for API params
  const formatDateForApi = (date: Date | null) => {
    if (!date) return '';
    return date.toISOString().split('T')[0];
  };

  // Fetch performance data
  const fetchPerformanceData = async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = {
        startDate: formatDateForApi(startDate),
        endDate: formatDateForApi(endDate)
      };

      // Get the current user's performance data
      const response = await trackingService.getMyPerformance(
        params.startDate,
        params.endDate
      );
      
      setPerformanceData(response.performance);
      setTotalStats(response.totals);

      // Prepare chart data
      prepareChartData(response.performance);
    } catch (error) {
      console.error('Error fetching performance data:', error);
      enqueueSnackbar('Failed to load performance data', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const fetchAllEmployeesData = async () => {
    setEmployeesLoading(true);
    try {
      const params: Record<string, string> = {
        startDate: formatDateForApi(startDate),
        endDate: formatDateForApi(endDate),
        compareEmployees: 'true'
      };
  
      // This endpoint needs to be created in your backend
      const response = await api.get('/api/employee/performance/compare', { params });
      
      if (response.data && response.data.employees) {
        setEmployeesData(response.data.employees);
        prepareEmployeeComparisonChart(response.data.employees);
      }
    } catch (error) {
      console.error('Error fetching employee comparison data:', error);
      enqueueSnackbar('Failed to load employee comparison data', { variant: 'error' });
    } finally {
      setEmployeesLoading(false);
    }
  };
  
  // Fetch monthly attendance (出勤時數) — owner-only endpoint.
  const fetchAttendance = async () => {
    setAttLoading(true);
    try {
      const res = await timeClockService.getMonthly(attMonth);
      setAttData(res.users);
    } catch (error) {
      console.error('Error fetching attendance data:', error);
      enqueueSnackbar('無法載入出勤資料', { variant: 'error' });
    } finally {
      setAttLoading(false);
    }
  };

  // Taipei wall clock ↔ ISO instant (Taiwan has no DST, so +08:00 is fixed).
  const toTaipeiIso = (local: string) => (local ? new Date(`${local}:00+08:00`).toISOString() : null);
  const openEditRow = (u: MonthlyUser, r: MonthlyDayRow) => {
    const inAt = r.in_time ? `${r.date}T${r.in_time}` : '';
    const outDate = r.out_date || r.date;
    const outAt = r.out_time ? `${outDate}T${r.out_time}` : '';
    setEditRow({ userId: u.user_id, name: u.employee_name, row: r, inAt, outAt, note: r.note || '' });
  };

  const saveEditRow = async () => {
    if (!editRow) return;
    const { row } = editRow;
    const inAt = toTaipeiIso(editRow.inAt);
    const outAt = toTaipeiIso(editRow.outAt);
    if (!inAt && !outAt) { enqueueSnackbar('請填上班或下班時間', { variant: 'warning' }); return; }
    setEditSaving(true);
    try {
      await timeClockService.editShift({
        userId: editRow.userId, inId: row.in_id, outId: row.out_id, inAt, outAt, note: editRow.note,
      });
      enqueueSnackbar('已更新出勤紀錄', { variant: 'success' });
      setEditRow(null);
      fetchAttendance();
    } catch (error: any) {
      enqueueSnackbar(error?.response?.data?.message || '更新失敗', { variant: 'error' });
    } finally {
      setEditSaving(false);
    }
  };

  const deleteRow = async (u: MonthlyUser, r: MonthlyDayRow) => {
    const when = `${r.date} ${r.in_time || '?'}–${r.out_time || '?'}`;
    if (!window.confirm(`刪除 ${u.employee_name} ${when} 這筆出勤？（舊資料會留存在紀錄中）`)) return;
    try {
      await timeClockService.deleteShift(r.in_id, r.out_id);
      enqueueSnackbar('已刪除', { variant: 'success' });
      fetchAttendance();
    } catch (error: any) {
      enqueueSnackbar(error?.response?.data?.message || '刪除失敗', { variant: 'error' });
    }
  };

  // Export attendance to CSV: 姓名,日期,上班,下班,時數 + per-person total rows.
  const exportAttendanceCSV = () => {
    const lines = ['姓名,日期,上班,下班,時數'];
    attData.forEach((u) => {
      const name = u.employee_name || `User ${u.user_id}`;
      u.rows.forEach((r) => {
        const inT = r.in_time || (r.anomaly ? '未打上班卡' : '');
        const outT = r.out_time || (r.open ? '未打下班卡' : '');
        const hrs = r.open || r.anomaly ? '' : r.hours.toFixed(2);
        lines.push(`${name},${r.date},${inT},${outT},${hrs}`);
      });
      lines.push(`${name},本月合計,,,${u.total_hours.toFixed(2)}`);
    });

    // Prepend BOM so Excel reads the Traditional Chinese as UTF-8.
    const csvContent = '﻿' + lines.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `attendance-${attMonth}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Add this function to prepare the employee comparison chart
  const prepareEmployeeComparisonChart = (data: EmployeeSummary[]) => {
    // Sort employees by total items (received + shipped) in descending order
    const sortedData = [...data].sort((a, b) => 
      (b.items_received + b.items_shipped) - (a.items_received + a.items_shipped)
    );
  
    // Limit to top 10 employees for readability
    const topEmployees = sortedData.slice(0, 10);
    
    const labels = topEmployees.map(emp => emp.employee_name || `User ${emp.user_id}`);
    
    const chartData: ChartData<'bar', number[], string> = {
      labels,
      datasets: [
        {
          label: 'Items Received',
          data: topEmployees.map(emp => emp.items_received),
          backgroundColor: 'rgba(54, 162, 235, 0.7)',
          borderColor: 'rgba(54, 162, 235, 1)',
          borderWidth: 1
        },
        {
          label: 'Items Shipped',
          data: topEmployees.map(emp => emp.items_shipped),
          backgroundColor: 'rgba(255, 159, 64, 0.7)',
          borderColor: 'rgba(255, 159, 64, 1)',
          borderWidth: 1
        },
        {
          label: 'Orders Shipped',
          data: topEmployees.map(emp => emp.orders_shipped),
          backgroundColor: 'rgba(75, 192, 192, 0.7)',
          borderColor: 'rgba(75, 192, 192, 1)',
          borderWidth: 1
        }
      ]
    };
    
    setEmployeeChartData(chartData);
  };

  // Fetch action data (detailed logs)
  const fetchActionData = async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = {
        startDate: startDate ? startDate.toISOString() : '',
        endDate: endDate ? endDate.toISOString() : '',
        limit: '100'
      };

      if (actionType !== 'all') {
        params.actionType = actionType;
      }

      const data = await trackingService.getMyActionLogs(
        params.startDate,
        params.endDate,
        params.actionType,
        parseInt(params.limit)
      );
      
      setActionData(data);
    } catch (error) {
      console.error('Error fetching action data:', error);
      enqueueSnackbar('Failed to load action logs', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Prepare chart data from performance data
  const prepareChartData = (data: PerformanceData[]) => {
    // Group data by date
    const dateMap = new Map<string, { received: number, shipped: number, itemsShipped: number }>();
    
    data.forEach(item => {
      const date = new Date(item.date).toLocaleDateString();
      const currentValue = dateMap.get(date) || { received: 0, shipped: 0, itemsShipped: 0 };
      
      dateMap.set(date, {
        received: currentValue.received + item.items_received,
        shipped: currentValue.shipped + item.orders_shipped,
        itemsShipped: currentValue.itemsShipped + item.items_shipped
      });
    });

    // Sort dates
    const sortedDates = Array.from(dateMap.keys()).sort((a, b) => {
      return new Date(a).getTime() - new Date(b).getTime();
    });

    // Create chart data
    const chartData: ChartData<'line', number[], string> = {
      labels: sortedDates,
      datasets: [
        {
          label: 'Items Received',
          data: sortedDates.map(date => dateMap.get(date)?.received || 0),
          backgroundColor: 'rgba(54, 162, 235, 0.5)',
          borderColor: 'rgba(54, 162, 235, 1)',
          borderWidth: 1
        },
        {
          label: 'Orders Shipped',
          data: sortedDates.map(date => dateMap.get(date)?.shipped || 0),
          backgroundColor: 'rgba(75, 192, 192, 0.5)',
          borderColor: 'rgba(75, 192, 192, 1)',
          borderWidth: 1
        },
        {
          label: 'Items Shipped',
          data: sortedDates.map(date => dateMap.get(date)?.itemsShipped || 0),
          backgroundColor: 'rgba(255, 159, 64, 0.5)',
          borderColor: 'rgba(255, 159, 64, 1)',
          borderWidth: 1
        }
      ]
    };
    
    setChartData(chartData);
  };

  // Handle filter change and refresh data
  const handleRefresh = () => {
    if (tabValue === 0) {
      fetchPerformanceData();
    } else if (tabValue === 2) {
      fetchAllEmployeesData();
    } else if (tabValue === 3) {
      fetchAttendance();
    } else {
      fetchActionData();
    }
  };

  // Export to CSV
  const exportToCSV = () => {
    try {
      let csvContent = '';
      
      if (tabValue === 0 && performanceData.length > 0) {
        // Add header row
        const headers = Object.keys(performanceData[0])
          .filter(key => key !== 'additional_metrics') // Skip complex object
          .join(',');
        csvContent += headers + '\n';
        
        // Add data rows
        performanceData.forEach(row => {
          const values = Object.keys(row)
            .filter(key => key !== 'additional_metrics')
            .map(key => {
              const value = (row as any)[key];
              // Handle values that need quotes (strings with commas)
              if (typeof value === 'string' && value.includes(',')) {
                return `"${value}"`;
              }
              return value;
            })
            .join(',');
          csvContent += values + '\n';
        });
      } else if (tabValue === 1 && actionData.length > 0) {
        // Add header row
        const headers = Object.keys(actionData[0])
          .filter(key => key !== 'additional_data')
          .join(',');
        csvContent += headers + '\n';
        
        // Add data rows
        actionData.forEach(row => {
          const values = Object.keys(row)
            .filter(key => key !== 'additional_data')
            .map(key => {
              const value = (row as any)[key];
              if (typeof value === 'string' && value.includes(',')) {
                return `"${value}"`;
              }
              return value;
            })
            .join(',');
          csvContent += values + '\n';
        });
      }
      
      if (csvContent) {
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', `employee-${tabValue === 0 ? 'performance' : 'actions'}-${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error('Error exporting to CSV:', error);
      enqueueSnackbar('Failed to export data', { variant: 'error' });
    }
  };

  // Load initial data
  useEffect(() => {
    if (tabValue === 0) {
      fetchPerformanceData();
    } else if (tabValue === 1) {
      fetchActionData();
    } else if (tabValue === 2) {
      fetchAllEmployeesData();
    }
  }, [tabValue, startDate, endDate]);

  // Attendance tab reloads on its own month control (not the shared date range).
  useEffect(() => {
    if (tabValue === 3) fetchAttendance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabValue, attMonth]);

  // Render performance summary cards
  const renderSummaryCards = () => {
    // Calculate daily averages
    const dateDiff = endDate && startDate ? 
      Math.max(1, Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24))) : 
      30;
    const dailyItemsReceived = totalStats.items_received / dateDiff;
    const dailyOrdersShipped = totalStats.orders_shipped / dateDiff;
    const dailyItemsShipped = totalStats.items_shipped / dateDiff;

    return (
      <Grid container spacing={3}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Items Received
              </Typography>
              <Typography variant="h4" component="div">
                {totalStats.items_received}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {dailyItemsReceived.toFixed(1)} per day
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Orders Shipped
              </Typography>
              <Typography variant="h4" component="div">
                {totalStats.orders_shipped}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {dailyOrdersShipped.toFixed(1)} per day
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Items Shipped
              </Typography>
              <Typography variant="h4" component="div">
                {totalStats.items_shipped}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {dailyItemsShipped.toFixed(1)} per day
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Date Range
              </Typography>
              <Typography variant="body1">
                {startDate?.toLocaleDateString()} - {endDate?.toLocaleDateString()}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {dateDiff} days
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  // Get action type label
  const getActionTypeLabel = (type: string) => {
    switch (type) {
      case 'ITEM_RECEIVED': return 'Item Received';
      case 'ORDER_SHIPPED': return 'Order Shipped';
      case 'SHIPPING_DOC_PRINTED': return 'Shipping Doc Printed';
      default: return type;
    }
  };

  return (
    <Box sx={{ width: '100%', pb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Employee Performance
        </Typography>
        
        <Button
          variant="contained"
          color="secondary"
          startIcon={<DownloadIcon />}
          onClick={tabValue === 3 ? exportAttendanceCSV : exportToCSV}
          disabled={
            (tabValue === 0 && performanceData.length === 0) ||
            (tabValue === 1 && actionData.length === 0) ||
            (tabValue === 3 && attData.length === 0)
          }
        >
          Export CSV
        </Button>
      </Box>
      
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 3 }}>
          {tabValue === 3 ? (
            <TextField
              label="月份"
              type="month"
              value={attMonth}
              onChange={(e) => setAttMonth(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          ) : (
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={(newValue: Date | null) => setStartDate(newValue)}
              />

              <DatePicker
                label="End Date"
                value={endDate}
                onChange={(newValue: Date | null) => setEndDate(newValue)}
              />
            </LocalizationProvider>
          )}

          {tabValue === 1 && (
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel id="action-type-label">Action Type</InputLabel>
              <Select
                labelId="action-type-label"
                value={actionType}
                label="Action Type"
                onChange={(e: SelectChangeEvent) => setActionType(e.target.value)}
              >
                <MenuItem value="all">All Actions</MenuItem>
                <MenuItem value="ITEM_RECEIVED">Items Received</MenuItem>
                <MenuItem value="ORDER_SHIPPED">Orders Shipped</MenuItem>
                <MenuItem value="SHIPPING_DOC_PRINTED">Shipping Documents</MenuItem>
              </Select>
            </FormControl>
          )}
          
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Refresh'}
          </Button>
        </Box>
        
        <Tabs value={tabValue} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tab label="Performance Summary" icon={<AssessmentIcon />} iconPosition="start" />
            <Tab label="Detailed Actions" icon={<TrendingUpIcon />} iconPosition="start" />
            <Tab label="Employee Comparison" icon={<PersonIcon />} iconPosition="start" />
            {currentUser?.id === 1 && (
              <Tab label="出勤時數" icon={<AccessTimeIcon />} iconPosition="start" />
            )}
            </Tabs>

      </Paper>
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box>
          {tabValue === 0 && (
            <>
              {renderSummaryCards()}
              
              <Paper sx={{ p: 2, mt: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Performance Trends
                </Typography>
                <Box sx={{ height: 400 }}>
                  <Line 
                    data={chartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        y: {
                          beginAtZero: true
                        }
                      }
                    }}
                  />
                </Box>
              </Paper>
              
              <Paper sx={{ p: 2, mt: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Performance Details
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell align="right">Items Received</TableCell>
                        <TableCell align="right">Orders Shipped</TableCell>
                        <TableCell align="right">Items Shipped</TableCell>
                        <TableCell align="right">Last Updated</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {performanceData.map((row) => (
                        <TableRow key={`${row.user_id}-${row.date}`}>
                          <TableCell>{new Date(row.date).toLocaleDateString()}</TableCell>
                          <TableCell align="right">{row.items_received}</TableCell>
                          <TableCell align="right">{row.orders_shipped}</TableCell>
                          <TableCell align="right">{row.items_shipped}</TableCell>
                          <TableCell align="right">
                            {new Date(row.updated_at).toLocaleString()}
                          </TableCell>
                        </TableRow>
                      ))}
                      {performanceData.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={5} align="center">
                            No performance data found for the selected period
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </>
          )}
          
          {tabValue === 1 && (
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Action Logs
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Timestamp</TableCell>
                      <TableCell>Action Type</TableCell>
                      <TableCell>Order ID</TableCell>
                      <TableCell>Item ID</TableCell>
                      <TableCell align="right">Quantity</TableCell>
                      <TableCell>Details</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {actionData.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell>
                          {new Date(row.action_timestamp).toLocaleString()}
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={getActionTypeLabel(row.action_type)}
                            color={
                              row.action_type === 'ITEM_RECEIVED' ? 'success' :
                              row.action_type === 'ORDER_SHIPPED' ? 'primary' :
                              'default'
                            }
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{row.order_id}</TableCell>
                        <TableCell>{row.item_id || '-'}</TableCell>
                        <TableCell align="right">{row.quantity || 1}</TableCell>
                        <TableCell>
                          {row.additional_data && (
                            <Tooltip title={JSON.stringify(row.additional_data, null, 2)}>
                              <IconButton size="small">
                                <AssessmentIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    {actionData.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          No action logs found for the selected filters
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
          {tabValue === 2 && (
  <>
    <Box sx={{ mt: 3, mb: 4 }}>
      <Alert severity="info" sx={{ mb: 2 }}>
        Comparing performance metrics across all employees for the selected date range.
      </Alert>
    </Box>
    
    {employeesLoading ? (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    ) : (
      <>
        <Paper sx={{ p: 2, mt: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Employee Performance Comparison
          </Typography>
          <Box sx={{ height: 400 }}>
            <Bar 
              data={employeeChartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  title: {
                    display: true,
                    text: 'Top Performers'
                  },
                  legend: {
                    position: 'top',
                  },
                },
                scales: {
                  y: {
                    beginAtZero: true
                  }
                },
              }}
            />
          </Box>
        </Paper>
        
        <Paper sx={{ p: 2, mt: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Employee Performance Details
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Employee</TableCell>
                  <TableCell align="right">Items Received</TableCell>
                  <TableCell align="right">Orders Shipped</TableCell>
                  <TableCell align="right">Items Shipped</TableCell>
                  <TableCell align="right">Total Items</TableCell>
                  <TableCell align="right">Efficiency (items/day)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {employeesData
                  .sort((a, b) => (b.items_received + b.items_shipped) - (a.items_received + a.items_shipped))
                  .map((employee) => (
                    <TableRow key={employee.user_id}>
                      <TableCell>{employee.employee_name || `User ${employee.user_id}`}</TableCell>
                      <TableCell align="right">{employee.items_received}</TableCell>
                      <TableCell align="right">{employee.orders_shipped}</TableCell>
                      <TableCell align="right">{employee.items_shipped}</TableCell>
                      <TableCell align="right">{employee.items_received + employee.items_shipped}</TableCell>
                      <TableCell align="right">
                        {employee.efficiency.toFixed(1)}
                        <Tooltip title="Items processed per day during the selected period">
                          <IconButton size="small">
                            <AssessmentIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                {employeesData.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No employee data found for the selected period
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </>
    )}
  </>
)}
          {tabValue === 3 && currentUser?.id === 1 && (
            <Box>
              <Alert severity="info" sx={{ mb: 3 }}>
                {attMonth} 出勤時數與本月目前薪資（跨日下班會配對到上班那天；未打卡、打卡錯誤會列在異常提醒）。
                時薪 200、國定假日雙倍；Adam 月薪制依薪資單規則計算加班。尚未下班的班次不計入。
              </Alert>

              {attLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : attData.length === 0 ? (
                <Paper sx={{ p: 3, textAlign: 'center' }}>
                  <Typography color="textSecondary">此月份沒有打卡紀錄</Typography>
                </Paper>
              ) : (
                <>
                  <Paper sx={{ p: 2, mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>薪資試算（本月至今）</Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>姓名</TableCell>
                            <TableCell>類型</TableCell>
                            <TableCell align="right">時數</TableCell>
                            <TableCell align="right">應發</TableCell>
                            <TableCell align="right">扣款</TableCell>
                            <TableCell align="right">實發</TableCell>
                            <TableCell>異常</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {attData.map((u) => {
                            const p = u.payroll;
                            const deduct = p ? p.deductions.reduce((s, d) => s + d.amount, 0) : 0;
                            const errors = p ? p.alerts.filter((a) => a.level === 'error').length : 0;
                            const warnings = p ? p.alerts.filter((a) => a.level === 'warning').length : 0;
                            return (
                              <TableRow key={u.user_id}>
                                <TableCell>{u.employee_name || `User ${u.user_id}`}</TableCell>
                                <TableCell>{p?.type === 'monthly' ? '月薪' : '時薪'}</TableCell>
                                <TableCell align="right">{u.total_hours.toFixed(2)}</TableCell>
                                <TableCell align="right">{p ? p.gross.toLocaleString() : '-'}</TableCell>
                                <TableCell align="right">{p ? (deduct ? `−${deduct.toLocaleString()}` : '0') : '-'}</TableCell>
                                <TableCell align="right"><strong>{p ? p.net.toLocaleString() : '-'}</strong></TableCell>
                                <TableCell>
                                  {errors > 0 && <Chip size="small" color="error" label={`${errors} 錯誤`} sx={{ mr: 0.5 }} />}
                                  {warnings > 0 && <Chip size="small" color="warning" label={`${warnings} 待確認`} />}
                                  {errors === 0 && warnings === 0 && <Chip size="small" color="success" label="正常" />}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                          <TableRow>
                            <TableCell colSpan={3}><strong>合計</strong></TableCell>
                            <TableCell align="right">
                              <strong>{attData.reduce((s, u) => s + (u.payroll?.gross || 0), 0).toLocaleString()}</strong>
                            </TableCell>
                            <TableCell />
                            <TableCell align="right">
                              <strong>{attData.reduce((s, u) => s + (u.payroll?.net || 0), 0).toLocaleString()}</strong>
                            </TableCell>
                            <TableCell />
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>

                  {attData.map((u) => {
                    const p = u.payroll;
                    const problems = p ? p.alerts.filter((a) => a.level !== 'info') : [];
                    const notes = p ? p.alerts.filter((a) => a.level === 'info') : [];
                    return (
                      <Paper key={u.user_id} sx={{ p: 2, mb: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                          <Typography variant="h6">
                            {u.employee_name || `User ${u.user_id}`}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <Chip label={`本月合計 ${u.total_hours.toFixed(2)} 小時`} />
                            {p && <Chip color="primary" label={`目前實發 NT$${p.net.toLocaleString()}`} />}
                            {p && (
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<DownloadIcon />}
                                onClick={() => downloadPayslip(u, attMonth).catch((err) => {
                                  console.error('Payslip export failed:', err);
                                  enqueueSnackbar('薪資單下載失敗', { variant: 'error' });
                                })}
                              >
                                薪資單 Excel
                              </Button>
                            )}
                          </Box>
                        </Box>

                        {problems.map((a, i) => (
                          <Alert key={`p-${i}`} severity={a.level === 'error' ? 'error' : 'warning'} sx={{ mb: 1 }}>
                            {a.date && <strong>{a.date.slice(5)}　</strong>}{a.message}
                          </Alert>
                        ))}
                        {notes.map((a, i) => (
                          <Alert key={`n-${i}`} severity="info" sx={{ mb: 1, py: 0 }}>
                            {a.date && <strong>{a.date.slice(5)}　</strong>}{a.message}
                          </Alert>
                        ))}

                        {p && (
                          <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap', my: 2 }}>
                            <Box>
                              {p.lines.filter((l) => l.amount !== 0).map((l) => (
                                <Typography key={l.label} variant="body2">
                                  ＋ {l.label}：{l.amount.toLocaleString()}
                                </Typography>
                              ))}
                              <Typography variant="body2"><strong>應發 {p.gross.toLocaleString()}</strong></Typography>
                            </Box>
                            <Box>
                              {p.deductions.length === 0 ? (
                                <Typography variant="body2" color="textSecondary">無扣款</Typography>
                              ) : p.deductions.map((d) => (
                                <Typography key={d.label} variant="body2">
                                  − {d.label}：{d.amount.toLocaleString()}
                                </Typography>
                              ))}
                              <Typography variant="body2"><strong>實發 {p.net.toLocaleString()}</strong></Typography>
                            </Box>
                          </Box>
                        )}

                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>日期</TableCell>
                                <TableCell>上班</TableCell>
                                <TableCell>下班</TableCell>
                                <TableCell align="right">時數</TableCell>
                                <TableCell>備註</TableCell>
                                <TableCell align="right" sx={{ width: 88 }} />
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {u.rows.map((r, idx) => {
                                const flagged = p?.alerts.some((a) => a.date === r.date && a.level !== 'info');
                                return (
                                  <TableRow key={`${r.date}-${idx}`} sx={flagged ? { bgcolor: 'rgba(237, 108, 2, 0.08)' } : undefined}>
                                    <TableCell>{r.date}</TableCell>
                                    <TableCell>
                                      {r.in_time || (r.anomaly
                                        ? <Typography component="span" color="error.main">⚠ 未打上班卡</Typography>
                                        : '-')}
                                    </TableCell>
                                    <TableCell>
                                      {r.out_time
                                        ? `${r.out_time}${r.out_date ? '（隔日）' : ''}`
                                        : (r.open
                                          ? <Typography component="span" color="warning.main">⚠ 未打下班卡</Typography>
                                          : '-')}
                                    </TableCell>
                                    <TableCell align="right">
                                      {r.open || r.anomaly ? '-' : r.hours.toFixed(2)}
                                    </TableCell>
                                    <TableCell sx={{ whiteSpace: 'pre-wrap' }}>{r.note || ''}</TableCell>
                                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                      {(r.in_id || r.out_id) ? (
                                        <>
                                          <Tooltip title="編輯">
                                            <IconButton size="small" onClick={() => openEditRow(u, r)}><EditIcon fontSize="small" /></IconButton>
                                          </Tooltip>
                                          <Tooltip title="刪除">
                                            <IconButton size="small" color="error" onClick={() => deleteRow(u, r)}><DeleteIcon fontSize="small" /></IconButton>
                                          </Tooltip>
                                        </>
                                      ) : null}
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Paper>
                    );
                  })}
                </>
              )}
            </Box>
          )}
        </Box>
      )}
      <Dialog open={!!editRow} onClose={() => !editSaving && setEditRow(null)} maxWidth="xs" fullWidth>
        <DialogTitle>編輯出勤 — {editRow?.name} {editRow?.row.date}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
          <TextField
            label="上班" type="datetime-local" size="small" InputLabelProps={{ shrink: true }}
            value={editRow?.inAt || ''}
            onChange={(e) => editRow && setEditRow({ ...editRow, inAt: e.target.value })}
            helperText={!editRow?.row.in_id ? '原本沒有上班卡，填入即補登' : ' '}
          />
          <TextField
            label="下班" type="datetime-local" size="small" InputLabelProps={{ shrink: true }}
            value={editRow?.outAt || ''}
            onChange={(e) => editRow && setEditRow({ ...editRow, outAt: e.target.value })}
            helperText={!editRow?.row.out_id ? '原本沒有下班卡，填入即補登' : '跨日班請改日期'}
          />
          <TextField
            label="備註" size="small" multiline minRows={2}
            value={editRow?.note || ''}
            onChange={(e) => editRow && setEditRow({ ...editRow, note: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditRow(null)} disabled={editSaving}>取消</Button>
          <Button variant="contained" onClick={saveEditRow} disabled={editSaving}>
            {editSaving ? <CircularProgress size={20} /> : '儲存'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmployeePerformancePage;